/**
 * Hộp "Thông báo Lark" ở Platform → Góp ý của khách: nhập Webhook URL của bot (+ khoá ký) để mỗi góp ý mới báo vào nhóm Lark.
 * Backend mã hoá và chỉ trả URL dạng che, nên ô nhập luôn trống: để trống = giữ giá trị đã lưu.
 */
import { useState } from 'react'
import { toast } from 'sonner'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox, Switch } from '@/components/ui/misc'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { platformApiError } from '@/lib/platform-client'
import { cn } from '@/lib/utils'
import { useLarkSettings, useSaveLarkSettings, useTestLark, type LarkSettings } from '@/hooks/use-platform-feedback'

const SOURCE_NOTE: Record<LarkSettings['source'], string> = {
  settings: 'Đang dùng cấu hình lưu tại đây.',
  env: 'Đang dùng cấu hình khai ở máy chủ (biến môi trường). Lưu ở đây sẽ thay thế nó.',
  none: 'Chưa cấu hình — góp ý mới chưa được báo vào Lark.',
}

export function LarkStatusDot({ active }: { active: boolean | undefined }) {
  return <span aria-hidden className={cn('h-2 w-2 rounded-full', active ? 'bg-emerald-500' : 'bg-muted-foreground/40')} />
}

export function LarkSettingsDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { data, isLoading, isError } = useLarkSettings(open)
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose() }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Thông báo Lark khi có góp ý mới</DialogTitle>
          <DialogDescription>
            Mỗi góp ý mới sẽ được báo vào nhóm Lark qua Custom Bot. Tạo bot: Cài đặt nhóm → Bot → Thêm bot → Custom Bot, rồi dán Webhook URL vào đây.
          </DialogDescription>
        </DialogHeader>
        {isLoading && <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>}
        {isError && <p className="py-6 text-center text-sm text-destructive">Không tải được cấu hình</p>}
        {data && <LarkForm key={data.updatedAt ?? 'new'} settings={data} onClose={onClose} />}
      </DialogContent>
    </Dialog>
  )
}

function LarkForm({ settings, onClose }: { settings: LarkSettings; onClose: () => void }) {
  const save = useSaveLarkSettings()
  const test = useTestLark()
  // Chưa có cấu hình nào → mặc định BẬT, để dán URL + Lưu là chạy ngay (không lưu ở trạng thái tắt mà không hay).
  const initialEnabled = settings.source === 'none' ? true : settings.enabled
  const [enabled, setEnabled] = useState(initialEnabled)
  const [url, setUrl] = useState('')
  const [secret, setSecret] = useState('')
  const [clearSecret, setClearSecret] = useState(false)
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null)

  const needUrl = !url.trim() && !settings.hasUrl && !(settings.source === 'env')
  const dirty = !!url.trim() || !!secret.trim() || clearSecret || enabled !== initialEnabled
  const canSave = dirty && !needUrl && !save.isPending

  const doSave = () =>
    save.mutate(
      { enabled, webhookUrl: url.trim() || undefined, secret: secret.trim() || undefined, clearSecret: clearSecret || undefined },
      {
        onSuccess: () => { toast.success('Đã lưu cấu hình Lark'); setUrl(''); setSecret(''); setClearSecret(false); setResult(null) },
        onError: (e) => toast.error(platformApiError(e)),
      },
    )

  const doTest = () => {
    setResult(null)
    test.mutate(
      { webhookUrl: url.trim() || undefined, secret: url.trim() ? secret.trim() : undefined },
      {
        onSuccess: (r) => setResult({ ok: r.ok, text: r.ok ? 'Đã gửi tin thử — kiểm tra nhóm Lark.' : r.error ?? 'Không gửi được' }),
        onError: (e) => setResult({ ok: false, text: platformApiError(e) }),
      },
    )
  }

  const doClear = () => {
    if (!window.confirm('Xoá cấu hình Lark đã lưu? Góp ý mới sẽ không còn được báo (trừ khi máy chủ còn khai biến môi trường).')) return
    save.mutate({ clear: true }, {
      onSuccess: () => { toast.success('Đã xoá cấu hình Lark'); setEnabled(true) },
      onError: (e) => toast.error(platformApiError(e)),
    })
  }

  return (
    <div className="space-y-4">
      <div className="rounded-md bg-muted/60 px-3 py-2 text-sm">
        <div className="flex items-center gap-2 font-medium">
          <LarkStatusDot active={settings.active} />
          {settings.active ? 'Đang báo vào Lark' : 'Chưa báo vào Lark'}
        </div>
        <p className="mt-0.5 text-xs text-muted-foreground">{SOURCE_NOTE[settings.source]}</p>
      </div>

      <div className="flex items-center justify-between gap-3">
        <Label htmlFor="lark-enabled" className="font-normal">Báo góp ý mới vào Lark</Label>
        <Switch id="lark-enabled" checked={enabled} onCheckedChange={setEnabled} />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="lark-url">Webhook URL của bot</Label>
        <Input
          id="lark-url" value={url} onChange={(e) => setUrl(e.target.value)} autoComplete="off" spellCheck={false}
          placeholder={settings.urlMasked ?? 'https://open.larksuite.com/open-apis/bot/v2/hook/…'}
        />
        <p className="text-xs text-muted-foreground">
          {settings.hasUrl ? 'Đã lưu (che): để trống để giữ URL hiện tại, nhập URL mới để thay.' : 'Phải là https và thuộc larksuite.com, feishu.cn hoặc larkoffice.com. URL chứa token — đừng chia sẻ.'}
        </p>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="lark-secret">Khoá ký (nếu bot bật “Xác minh chữ ký”)</Label>
        <Input
          id="lark-secret" type="password" value={secret} onChange={(e) => { setSecret(e.target.value); if (e.target.value) setClearSecret(false) }}
          autoComplete="new-password" placeholder={settings.hasSecret ? '•••••••• (đã lưu — để trống để giữ)' : 'Không bắt buộc'}
        />
        {settings.hasSecret && (
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            <Checkbox checked={clearSecret} onCheckedChange={(v) => { setClearSecret(v === true); if (v === true) setSecret('') }} />
            Xoá khoá ký đã lưu
          </label>
        )}
      </div>

      {result && (
        <p role="status" className={cn('rounded-md px-3 py-2 text-sm', result.ok ? 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300' : 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300')}>
          {result.text}
        </p>
      )}

      <DialogFooter className="gap-2 sm:justify-between">
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={doTest} disabled={test.isPending || (!url.trim() && !settings.hasUrl && !settings.envConfigured)}>
            {test.isPending && <Loader2 className="animate-spin" />}Gửi thử
          </Button>
          {settings.hasUrl && <Button type="button" variant="ghost" className="text-destructive" onClick={doClear} disabled={save.isPending}>Xoá cấu hình</Button>}
        </div>
        <div className="flex gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>Đóng</Button>
          <Button type="button" onClick={doSave} disabled={!canSave}>
            {save.isPending && <Loader2 className="animate-spin" />}Lưu
          </Button>
        </div>
      </DialogFooter>
    </div>
  )
}
